import { useEffect, useState } from 'react';
import {
  Box,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { GroupEntity } from '@backstage/catalog-model';

export interface OwnsAssetsToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
  /** Machine ID of the team being edited — omitted on create, since a
   * brand-new team can never already have children. */
  machineId?: string;
}

/**
 * Marks a team that owns assets — not limited to dev teams; ops, security,
 * infrastructure, and platform teams also use this flag (used for FinOps and
 * security-audit attribution). Enforced leaf-only server-side — only a leaf
 * team can own an asset for tagging purposes — this component mirrors that
 * by disabling turning the toggle ON when the team currently has children,
 * purely as a UX convenience; the server check is the real enforcement.
 * Turning it OFF is never disabled, even with children: that's the only
 * save that can recover a team stuck with ownsAssets=true + children (e.g.
 * from a child added before the reparent guard existed), so it must always
 * be possible regardless of leaf status.
 */
export function OwnsAssetsToggle({
  value,
  onChange,
  machineId,
}: OwnsAssetsToggleProps) {
  const catalogApi = useApi(catalogApiRef);
  const [hasChildren, setHasChildren] = useState(false);
  const [loading, setLoading] = useState(Boolean(machineId));

  useEffect(() => {
    if (!machineId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: 'Group' },
        fields: ['metadata.name', 'metadata.annotations', 'spec.parent'],
      })
      .then(res => {
        if (cancelled) return;
        const active = (res.items as GroupEntity[]).filter(
          g => g.metadata.annotations?.['backstage.io/lifecycle'] !== 'deprecated',
        );
        setHasChildren(active.some(g => g.spec?.parent === machineId));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, machineId]);

  const disabled = loading || (hasChildren && !value);

  const toggle = (
    <FormControlLabel
      control={
        <Switch
          checked={value}
          disabled={disabled}
          onChange={e => onChange(e.target.checked)}
        />
      }
      label="Owns Assets"
    />
  );

  return (
    <Box>
      {hasChildren && !value ? (
        <Tooltip title="Only a leaf team with no child teams can be marked as owning assets">
          <span>{toggle}</span>
        </Tooltip>
      ) : (
        toggle
      )}
      <Typography variant="caption" color="textSecondary" display="block">
        Marks a team that owns assets — services, repos, infrastructure. Not
        limited to dev teams; ops, security, infrastructure, and platform
        teams use this too. Used for FinOps and security-audit reporting.
        Only available on leaf teams.
      </Typography>
    </Box>
  );
}
